import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { DashboardPanelInterface } from '@models/dashboard-panel-interface';
import { ElementSettingsService } from '@service/element-settings.service';
import { MimicService } from '@service/mimic.service';
import { ServerSideEventService } from '@service/serverSideEvent.service';
import { VesselService } from '@service/vessel.service';
import { ChannelFlags } from 'ops/common/models/device-flags';
import { ElementSettings, presetColorKeys } from 'ops/common/models/editor-viewer-element';
import { Mimic } from 'ops/common/models/mimic';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-dashboard-panel-mimics',
    templateUrl: './dashboard-panel-mimics.component.html',
    styleUrls: ['./dashboard-panel-mimics.component.scss'],
    standalone: false
})
export class DashboardPanelMimicsComponent implements DashboardPanelInterface, OnInit {
    LOADING_TIMEOUT = 1500;

    @Output() onDeleteClicked = new EventEmitter<void>();
    @Output() onResourceNotFound = new EventEmitter<void>();
    @Input() mimicId: string = null;
    @Input() isPreview: boolean = false;
    @ViewChild('mimicContainer') mimicContainer: ElementRef;

    selectedMimic: Mimic;
    maxHeight: string;
    maxWidth: string;
    elementsSettings: ElementSettings[];
    defaultColors = presetColorKeys;
    getUpdatedTagValue: Observable<ChannelFlags[]>;
    vesselId: string;

    isLoadingComplete = false;

    loadFailTimeout: NodeJS.Timeout = null;

    public mainContainerStyle = {
        'background-color': 'rgba(0,0,0,0)',
    };

    constructor(
        private mimicService: MimicService,
        private elementSettingsService: ElementSettingsService,
        private cdr: ChangeDetectorRef,
        private vesselService: VesselService,
        private serverSideEvents: ServerSideEventService,
    ) {}

    @HostListener('window:resize')
    handleWindowResize() {
        this.updateDivisionSize();
    }

    async ngOnInit(): Promise<void> {
        this.getUpdatedTagValue = this.serverSideEvents.onChannelDataReceived.asObservable();
        await this.switchMimic();
        const { id } = await this.vesselService.getVessel();
        this.vesselId = id;
    }

    async switchMimic(): Promise<void> {
        this.elementsSettings = await this.elementSettingsService.getAllByMimicId(this.mimicId);
        this.selectedMimic = await this.mimicService.getById(this.mimicId);
        this.setBackgroundColor(this.selectedMimic?.profile.bkcolor ?? this.getBackgroundColor());
        this.isLoadingComplete = true;

        if (this.selectedMimic == null) {
            this.startLoadFailTimeout();
        } else {
            this.cancelLoadFailTimeout();
        }

        this.cdr.detectChanges();
        this.updateDivisionSize();
    }

    updateDivisionSize() {
        if (this.mimicContainer) {
            this.maxHeight = this.mimicContainer.nativeElement.offsetHeight;
            this.maxWidth = this.mimicContainer.nativeElement.offsetWidth;
        }
    }

    handleDeleteClicked() {
        this.emitDeleteClicked();
    }

    emitDeleteClicked() {
        this.onDeleteClicked.emit();
    }

    startLoadFailTimeout() {
        if (!this.loadFailTimeout) {
            this.loadFailTimeout = setTimeout(() => {
                this.loadFailTimeout = null;
                this.emitResourceNotFound();
            }, this.LOADING_TIMEOUT);
        }
    }

    cancelLoadFailTimeout() {
        if (this.loadFailTimeout !== null) {
            clearTimeout(this.loadFailTimeout);
            this.loadFailTimeout = null;
        }
    }

    emitResourceNotFound() {
        this.onResourceNotFound.emit();
    }

    getBackgroundColor() {
        return this.mainContainerStyle['background-color'];
    }

    setBackgroundColor(color: string) {
        this.mainContainerStyle['background-color'] = color;
    }

    public get isViewInitialized() {
        return this.isLoadingComplete && this.loadFailTimeout === null;
    }
    public get isLoadingSpinnerVisible() {
        return !this.isLoadingComplete || this.loadFailTimeout !== null;
    }
    public get title() {
        return this.selectedMimic?.name;
    }
}
